/**
 * Authentication store for managing user authentication state and operations.
 * Handles MSAL integration, user session management, and role-based access control.
 *
 * This store is responsible for:
 * - Managing user authentication state (logged in/out)
 * - Handling MSAL (Microsoft Authentication Library) integration
 * - Managing user roles and privileges
 * - Supporting user impersonation
 * - Handling JWT token management
 * - Providing authorization checks
 * - Managing dynamic project-specific privileges
 */

'use client';

import { create } from 'zustand';
import { PublicClientApplication } from '@azure/msal-browser';
import { useConfigStore } from './configStore';
import { useBackendStore } from './backendStore';
import { PrivilegeEnum, UserRepository, PrivilegeDto, UserSettingsDto } from '@/api/ApiClient';
import { AuthRepository } from '@/api/AuthClient';
import { HttpClient } from '@/api/HttpClient';
import { isAuthorized as generalIsAuthorized } from '@/api/AuthHelper';
import { log } from '@/lib/utils/logger';
import { formatErrorMessage } from '@/lib/utils/apiErrors';
import {
  isStoredSessionExpirationValid,
  readUserdataFromStorage,
  removeUserDataFromStorage,
  clearMsalCacheFromStorage,
  storeUserData,
} from '@/lib/auth/userStorage';
import {
  buildShibbolethLogoutUrl,
  navigateToHome,
  resolveHomeUrl,
  resolveShibbolethLogoutReturnPath,
  type LogoutOptions,
  type LogoutScope,
} from '@/lib/auth/signOutFlow';
import { scopedSessionStorage } from '@/lib/browser/scopedStorage';
import { CsrfTokenProvider } from '@/api/AuthorizedApiBase';
import { userHasEppn, type SessionLostReason } from '@/lib/auth/sessionAuth';
import { invalidateCachedOption } from '@/lib/options/optionsCache';

export type { SessionLostReason };

/**
 * Available operations for privilege checks.
 * Defines the types of operations that can be performed on resources.
 */
export type Operation = 'create' | 'read' | 'update' | 'delete' | 'submit' | 'reset' | 'execute' | 'list';

export type EntityContextType = 'project' | 'sample' | 'dataset';

/**
 * Combined type for privilege checks that includes both the privilege enum
 * and an optional operation type.
 */
export type PrivilegeWithOperation = {
  /** The privilege to check */
  p: PrivilegeEnum;
  /** Optional operation type */
  op?: Operation;
};

/**
 * Interface representing entity-specific access rights.
 */
export interface EntityAccessRights {
  /** Entity ID */
  entityId: string;
  /** Entity type (project, sample, etc.) */
  entityType: EntityContextType;
  /** Additional privileges for this entity */
  privileges: PrivilegeDto[];
  /** When these rights were last loaded */
  lastLoaded: Date;
}

/**
 * How the current LiST session was established (from JWT claim, via GET /users/current).
 */
export type AuthMethod = 'msal' | 'shibboleth';

export type { LogoutScope, LogoutOptions };

/** Maps GET /users/current authMethod to a known IdP value; ignores unknown/legacy tokens. */
export function normalizeAuthMethod(value?: string | null): AuthMethod | undefined {
  if (value === 'msal' || value === 'shibboleth') {
    return value;
  }
  return undefined;
}

/**
 * Interface representing the current authenticated user's data.
 */
export interface ICurrentUser {
  /** Unique identifier for the user */
  id: string;
  /** When this is an anonymous user */
  isAnonymous: boolean;
  /** Optional education person principal name */
  eppn?: string;
  /** Sign-in path for this session (Entra vs Shibboleth); absent for anonymous */
  authMethod?: AuthMethod;
  /** User's full name */
  fullName: string;
  /** Optional profile / affiliation fields from GET /users/current */
  middleName?: string;
  email?: string;
  phone?: string;
  position?: string;
  orcId?: string;
  institution?: string;
  city?: string;
  state?: string;
  zip?: string;
  country?: string;
  /** Available roles for the user */
  roles: { id: number; name: string; isInhouse?: boolean }[];
  /** Currently selected role ID */
  selectedRole: number;
  /** Whether the user can switch between roles */
  canSwitchRoles: boolean;
  /** Whether the user is currently impersonating another user */
  usingSignInAs: boolean;
  /** Techniques a user has been assigned to */
  techniques: string[];
  /** Instruments (or simulation methods) a user is managing */
  managedInstruments: string[];
  /** User settings */
  settings: UserSettingsDto;
  /** Indicates if the user has access to any projects (either through global privileges or project-specific collaboration) */
  hasAnyProjectAccess: boolean;
  /** Indicates if the user has access to any samples (either through global privileges or project-specific collaboration) */
  hasAnySampleAccess: boolean;
  /** Research group faculty ID (for API key creation / filtering users by research group) */
  faculty?: string;
}

/**
 * Authentication state interface.
 * Defines the complete state and actions available in the auth store.
 *
 * @property {boolean} isLoading - Indicates if an authentication operation is in progress
 * @property {string | null} error - Current error message if any
 * @property {PrivilegeDto[]} privileges - User's current privileges
 * @property {ICurrentUser | undefined} currentUser - Current authenticated user information
 * @property {boolean} isInitialized - Store initialization state
 * @property {boolean} isClient - Client-side rendering indicator
 * @property {boolean} isInProgress - Authentication operation in progress indicator
 * @property {PublicClientApplication | null} msalInstance - MSAL instance for authentication
 * @property {Map<string, ProjectAccessRights>} projectPrivileges - Project-specific privileges cache
 * @property {string | null} currentProjectId - Currently active project ID for privilege context
 */
interface AuthState {
  /** Loading state indicator */
  isLoading: boolean;
  /** Current error message if any */
  error: string | null;
  /** User's current privileges */
  privileges: PrivilegeDto[];
  /** Current authenticated user information */
  currentUser: ICurrentUser | undefined;
  /** Store initialization state */
  isInitialized: boolean;
  /** Client-side rendering indicator */
  isClient: boolean;
  /** Authentication operation in progress indicator */
  isInProgress: boolean;
  /** MSAL instance */
  msalInstance: PublicClientApplication | null;
  /** Entity-specific privileges cache */
  entityPrivileges: Map<string, EntityAccessRights>;
  /** Currently active entity ID for privilege context */
  currentEntityId: string | null;
  /** Currently active entity type for privilege context */
  currentEntityType: EntityContextType | null;
  /** Entity privileges loading state - prevents multiple simultaneous API calls */
  entityPrivilegesLoading: Set<string>;
  /** Server error state for 500 errors (prevents automatic retries) */
  hasServerError: boolean;
  /** Server error status code */
  serverErrorStatus: number | null;
  /** Why the session-lost dialog is shown (only after user had eppn) */
  sessionLostReason: SessionLostReason;
  /** Session-lost dialog visibility */
  sessionLostDialogOpen: boolean;
  /** Viewing public data after explicit user choice */
  viewingPublicSession: boolean;
  clearViewingPublicSession: () => void;

  // Actions
  setIsLoading: (isLoading: boolean) => void;
  setError: (error: string | null) => void;
  setPrivileges: (privileges: PrivilegeDto[]) => void;
  setCurrentUser: (user: ICurrentUser | undefined) => void;
  setIsInitialized: (isInitialized: boolean) => void;
  setIsClient: (isClient: boolean) => void;
  setIsInProgress: (isInProgress: boolean) => void;
  setMsalInstance: (instance: PublicClientApplication) => void;
  setCurrentEntity: (entityId: string | null, entityType: EntityContextType | null) => void;
  /** Manually set the current entity context for privilege checking */
  setEntityContext: (entityId: string, entityType: EntityContextType) => void;

  /** Check if a user is authorized to perform an operation with a given resource
   * (i.e. can Delete a Sample, or see unpublished datea).
   * null/undefined allowed for convenience, to avoid null checks in the calling code
   * null/undefined is always denied access. */
  isAuthorized: (privilege: PrivilegeEnum | null | undefined, op: Operation) => boolean;
  /** Checks if a user has any of a set of privileges.
   * Usefull to check if app parts should be rendered which would just be empty otherwise,
   * like the Customizing are
   * */
  hasAnyPrivilege: (privileges: PrivilegeWithOperation[]) => boolean;

  // Dynamic privilege management
  /** Load entity-specific access rights */
  loadEntityPrivileges: (entityId: string, entityType: EntityContextType) => Promise<void>;
  /** Get combined privileges (static + entity-specific) */
  getCombinedPrivileges: () => PrivilegeDto[];
  /**
   * Check authorization with entity context
   * null/undefined allowed for convenience, to avoid null checks in the calling code
   * null/undefined is always denied access.
   */
  isAuthorizedInEntity: (
    privilege: PrivilegeEnum | null | undefined,
    op: Operation,
    entityId?: string,
    entityType?: EntityContextType
  ) => boolean;
  /** Clear entity privileges cache */
  clearEntityPrivileges: () => void;

  // Auth actions
  login: () => Promise<void>;
  /**
   * Full-page Microsoft sign-in (MSAL redirect). Use when login is not triggered by a direct user gesture
   * (e.g. HOC useEffect enforcing auth); popup flows are likely blocked there.
   * @param explicitReturnPath - Optional path starting with `/` (e.g. decoded `state` query). Defaults to current URL.
   */
  loginRedirect: (explicitReturnPath?: string) => Promise<void>;
  /**
   * Signs out of LiST and optionally the IdP (Entra / Shibboleth).
   * @param options.scope - `list` = community user, keep SSO; `full` = also end IdP session when known.
   */
  logout: (options?: LogoutOptions) => Promise<void>;
  /**
   * Allows users which the Admin privilege to become super users (all privileges).
   *
   * Users normally have a user role which does not give them all privileges,
   * they have to use this function to switch to a super user role.
   */
  switchRole: (selectedRole: number) => Promise<void>;
  /**
   * Impersonate a user. Server will return a new JWT with the impersonated user's privileges,
   * or 403 if the current user does not have the necessary privileges. (Or 404 if there is no user with the given ID).
   *
   * @param userId
   * @returns
   */
  impersonateUser: (userId: string | null) => Promise<void>;
  /**
   * Stop impersonating a user. This will throw 403 if the current user is not impersonating another user.
   */
  stopImpersonate: () => Promise<void>;
  /**
   * Get a token from MSAL, which can be exchanged for an app JWT.
   *
   * @returns
   */
  getToken: () => Promise<string | null>;

  /**
   * Single-flight bootstrap: ensures AuthToken cookie (jwt/app) exists before other API calls.
   * Hydrates user/privileges from storage or server. Sets {@link isInitialized} when done.
   */
  ensureAppAuthReady: () => Promise<void>;
  /**
   * @deprecated Use {@link ensureAppAuthReady}. Kept for tests and legacy callers.
   */
  initializeAuthData: () => Promise<void>;
  /**
   * Fetch a JWT cookie from the server. Should be followed by loading user data from the server.
   * TODO: Maybe make that one method?
   *
   * @param getToken
   * @returns
   */
  getJwt: (getToken?: () => Promise<string | null>) => Promise<string | null>;
  /**
   * Fetch user data from the server. This will also store the user data in local storage.
   *
   * @param options.allowCommunityFallback - When false, do not silently load community user on error (recovery paths).
   */
  fetchUserDataFromServer: (options?: { allowCommunityFallback?: boolean }) => Promise<void>;
  /**
   * After 401: refresh JWT and verify user still has eppn if they were signed in before.
   * Single-flight across concurrent callers.
   */
  attemptSessionRecovery: () => Promise<boolean>;
  /** Open session-lost dialog (only meaningful when user had been signed in). */
  notifySessionLost: (reason: NonNullable<SessionLostReason>) => void;
  clearSessionLost: () => void;
  /** User chose to continue as public/community after session loss. */
  acceptPublicSession: () => Promise<void>;
  /** Clear server error state and allow retries */
  clearServerError: () => void;
  /** Retry authentication after server error */
  retryAuth: () => Promise<void>;
}

/**
 * Authentication store hook.
 * Provides access to authentication state and operations.
 *
 * @example
 * function LoginButton() {
 *   const { login, isLoading } = useAuthStore();
 *   return (
 *     <button onClick={login} disabled={isLoading}>
 *       {isLoading ? 'Logging in...' : 'Login'}
 *     </button>
 *   );
 * }
 */
// Global loading promises to prevent race conditions
const loadingPromises = new Map<string, Promise<void>>();

/** Session key: pathname+search+hash (app-relative) to restore after MSAL redirect login (see loginRedirect). */
export const POST_MSAL_LOGIN_RETURN_PATH_KEY = 'list:postLoginReturn';

/** LiST-only sign-out: suppress MSAL token on jwt/app until explicit sign-in (survives page reload). */
export const VIEWING_PUBLIC_SESSION_KEY = 'list:viewingPublicSession';

function readViewingPublicSessionFlag(): boolean {
  if (typeof window === 'undefined') {
    return false;
  }
  try {
    return scopedSessionStorage.getItem(VIEWING_PUBLIC_SESSION_KEY) === '1';
  } catch {
    return false;
  }
}

function persistViewingPublicSession(active: boolean): void {
  if (typeof window === 'undefined') {
    return;
  }
  try {
    if (active) {
      scopedSessionStorage.setItem(VIEWING_PUBLIC_SESSION_KEY, '1');
    } else {
      scopedSessionStorage.removeItem(VIEWING_PUBLIC_SESSION_KEY);
    }
  } catch {
    /* ignore */
  }
}

function isViewingPublicSession(getState: () => AuthState): boolean {
  return getState().viewingPublicSession || readViewingPublicSessionFlag();
}

let sessionRecoveryPromise: Promise<boolean> | null = null;

/** Shared promise so jwt/app and user fetch run once per page load (deep-link race fix). */
let appAuthReadyPromise: Promise<void> | null = null;

export const useAuthStore = create<AuthState>()((set, get) => {
  return {
    isLoading: true,
    error: null,
    privileges: [],
    currentUser: undefined,
    isInitialized: false,
    isClient: false,
    isInProgress: false,
    msalInstance: null,
    entityPrivileges: new Map(),
    currentEntityId: null,
    currentEntityType: null,
    entityPrivilegesLoading: new Set(),
    hasServerError: false,
    serverErrorStatus: null,
    sessionLostReason: null,
    sessionLostDialogOpen: false,
    viewingPublicSession: false,

    clearViewingPublicSession: () => {
      persistViewingPublicSession(false);
      set({ viewingPublicSession: false });
    },

    // Basic actions
    setIsLoading: (isLoading) => set({ isLoading }),
    setError: (error) => set({ error }),
    setPrivileges: (privileges) => set({ privileges, isLoading: false }),
    setCurrentUser: (user) => set({ currentUser: user, isLoading: false }),
    setIsInitialized: (isInitialized) => set({ isInitialized }),
    setIsClient: (isClient) => set({ isClient }),
    setIsInProgress: (isInProgress) => set({ isInProgress }),
    setMsalInstance: (instance) => set({ msalInstance: instance }),
    setCurrentEntity: (entityId, entityType) => set({ currentEntityId: entityId, currentEntityType: entityType }),
    /** Manually set the current entity context for privilege checking */
    setEntityContext: (entityId, entityType) => {
      log.debug(`[AuthStore] Setting entity context to ${entityId} (${entityType})`);
      set({ currentEntityId: entityId, currentEntityType: entityType });
      // Automatically load privileges for this entity
      get().loadEntityPrivileges(entityId, entityType);
    },

    // Helper functions
    isAuthorized: (privilege: PrivilegeEnum | null | undefined, op: Operation) => {
      if (privilege == null) return false;
      const { privileges } = get();
      return generalIsAuthorized(privileges, privilege, op);
    },

    hasAnyPrivilege: (requiredPrivileges: PrivilegeWithOperation[]) => {
      return requiredPrivileges.some(({ p, op }) => get().isAuthorized(p, op ?? 'read'));
    },

    // Dynamic privilege management
    loadEntityPrivileges: async (entityId: string, entityType: EntityContextType) => {
      const entityKey = `${entityId}-${entityType}`;

      // Check if already loading using global promise to prevent race conditions
      if (loadingPromises.has(entityKey)) {
        log.debug(`[AuthStore] Already loading entity privileges for ${entityKey}, waiting for existing promise`);
        await loadingPromises.get(entityKey);
        return;
      }

      // Create a new loading promise
      const loadingPromise = (async () => {
        log.debug(`[AuthStore] Starting to load entity privileges for ${entityKey}`);

        // Check cache first
        const existing = get().entityPrivileges.get(entityKey);
        if (existing && Date.now() - existing.lastLoaded.getTime() < 1000 * 60 * 60) {
          // Cache for 1 hour
          log.debug(`[AuthStore] Using cached entity privileges for entity ${entityId} (${entityType})`);
          return; // Return early - no loading state was set
        }

        // Cache miss - set loading state and fetch from API
        set((state) => ({
          entityPrivilegesLoading: new Set(state.entityPrivilegesLoading).add(entityKey),
        }));

        log.debug(`[AuthStore] Cache miss - loading entity privileges for entity ${entityId} (${entityType})`);
        try {
          // Use the existing backend infrastructure
          const backend = useBackendStore.getState().getBackend();

          let accessRightResponse: PrivilegeDto[] | null = null;

          // Use the appropriate API repository based on entity type
          switch (entityType) {
            case 'project':
              // const projectRepo = new ProjectApiRepository(authContext, baseUrlNoTrailingSlash, http);
              accessRightResponse = await backend.project.getAccessRights(entityId);
              break;
            case 'sample':
              accessRightResponse = await backend.sample.getAccessRights(entityId);
              break;
            case 'dataset':
              accessRightResponse = await backend.data.getAccessRights(entityId);
              break;
            default:
              log.warn(`[AuthStore] Unsupported entity type: ${entityType}`);
              // Don't return early - let the finally block clear the loading state
              accessRightResponse = null;
          }

          if (accessRightResponse) {
            set({
              entityPrivileges: new Map(get().entityPrivileges).set(entityKey, {
                entityId: entityId,
                entityType: entityType,
                privileges: accessRightResponse,
                lastLoaded: new Date(),
              }),
              currentEntityId: entityId,
              currentEntityType: entityType,
            });
            log.debug(`[AuthStore] Entity privileges loaded for entity ${entityId} (${entityType})`);
          } else {
            log.warn(
              `[AuthStore] Failed to load entity privileges for entity ${entityId} (${entityType}) - no privileges in response`
            );

            const newMap = new Map(get().entityPrivileges);
            newMap.delete(entityKey);
            set({
              entityPrivileges: newMap,
              // Don't clear the entity context on API failure - keep it for fallback to static privileges
            });
            // Promise resolves successfully even with no privileges - this allows waiting components to continue
          }
        } catch (error) {
          log.error(`[AuthStore] Error loading entity privileges for entity ${entityId} (${entityType}):`, error);
          const newMap = new Map(get().entityPrivileges);
          newMap.delete(entityKey);
          set({
            entityPrivileges: newMap,
            // Don't clear the entity context on API error - keep it for fallback to static privileges
          });
          // Promise resolves even on error - this allows waiting components to continue
        } finally {
          // Clear loading state
          set((state) => {
            const newLoadingSet = new Set(state.entityPrivilegesLoading);
            newLoadingSet.delete(entityKey);
            return { entityPrivilegesLoading: newLoadingSet };
          });
          log.debug(`[AuthStore] Done loading entity privileges for ${entityKey}`);
        }
      })();

      // Store the promise immediately to prevent race conditions
      loadingPromises.set(entityKey, loadingPromise);
      try {
        await loadingPromise;
      } finally {
        loadingPromises.delete(entityKey);
      }
    },

    getCombinedPrivileges: () => {
      const staticPrivileges = get().privileges;
      const entityPrivilegesArray = Array.from(get().entityPrivileges.values());
      const combinedPrivileges = [...staticPrivileges];

      for (const entity of entityPrivilegesArray) {
        combinedPrivileges.push(...entity.privileges);
      }

      return combinedPrivileges;
    },

    isAuthorizedInEntity: (
      privilege: PrivilegeEnum | null | undefined,
      op: Operation,
      entityId?: string,
      entityType?: EntityContextType
    ) => {
      if (privilege == null) return false;
      const entityIdToCheck = entityId || get().currentEntityId;
      const entityTypeToCheck = entityType || get().currentEntityType;

      if (!entityIdToCheck || !entityTypeToCheck) {
        // No need to spam the log - normal for things outside of sample/project tree.
        // Eg. search and grid.
        return get().isAuthorized(privilege, op);
      }

      const entityKey = `${entityIdToCheck}-${entityTypeToCheck}`;
      const entityPrivileges = get().entityPrivileges.get(entityKey);
      const isLoading = get().entityPrivilegesLoading.has(entityKey);

      if (isLoading) {
        log.debug(`[AuthStore] Entity privileges loading for ${entityKey}, denying access until loaded`);
        return false; // Conservative approach - deny access while loading
      }

      if (!entityPrivileges) {
        log.warn(`[AuthStore] Entity privileges not available for ${entityKey}, denying access`);
        return false; // No privileges available - deny access
      }

      const result = generalIsAuthorized(entityPrivileges.privileges, privilege, op);
      return result;
    },

    clearEntityPrivileges: () => {
      log.debug('[AuthStore] Clearing entity privileges cache');
      set({ entityPrivileges: new Map(), currentEntityId: null, currentEntityType: null });
      log.debug('[AuthStore] Entity privileges cache cleared.');
    },

    /**
     * Ensures the app AuthToken cookie and user/privileges are ready before any other API traffic.
     * Single-flight: concurrent callers await the same promise (MsalProvider, session recovery, etc.).
     */
    ensureAppAuthReady: async () => {
      if (get().isInitialized && !appAuthReadyPromise) {
        return;
      }
      if (appAuthReadyPromise) {
        return appAuthReadyPromise;
      }

      appAuthReadyPromise = (async () => {
        get().setIsLoading(true);
        try {
          if (readViewingPublicSessionFlag()) {
            set({ viewingPublicSession: true });
          }

          const storedData = readUserdataFromStorage();
          const storageExpired = !storedData?.expiration || Date.now() > new Date(storedData.expiration).getTime();

          if (!storageExpired && storedData) {
            const wasSignedIn = userHasEppn(storedData.user);
            set({
              privileges: storedData.privileges,
              currentUser: storedData.user,
              error: null,
            });
            if (wasSignedIn) {
              await get().attemptSessionRecovery();
              return;
            }
            // Cached community profile: refresh LiST JWT from cookie only — not MSAL while Entra SSO stays alive.
            log.debug('[AuthStore] Valid cached user; issuing app JWT before API calls.');
            await get().getJwt(undefined);
            return;
          }

          log.debug('[AuthStore] No user data in storage or token expired. Fetching new JWT and user data.');
          const msalForJwt = isViewingPublicSession(get) ? undefined : get().getToken;
          const tokenExpiration = await get().getJwt(msalForJwt);
          if (tokenExpiration) {
            await get().fetchUserDataFromServer();
          }
        } catch (error) {
          log.error('[AuthStore] ensureAppAuthReady failed:', error);
          appAuthReadyPromise = null;
        } finally {
          get().setIsInitialized(true);
          get().setIsLoading(false);
        }
      })();

      return appAuthReadyPromise;
    },

    initializeAuthData: async () => {
      return get().ensureAppAuthReady();
    },

    /**
     * Gets a JWT token for the current user context.
     * For authenticated users, this will either use MSAL token or refresh the app JWT.
     *
     * @param getToken - Function to get MSAL token
     * @returns Token expiration timestamp or null if failed
     * @throws {Error} If JWT acquisition fails
     */
    getJwt: async (getToken) => {
      const configStore = useConfigStore.getState();
      if (!configStore.systemConfig) return null;

      const baseUrlNoTrailingSlash = configStore.getBaseUrlNoTrailingSlash();
      const http = new HttpClient();
      http.setBaseUrl(baseUrlNoTrailingSlash);
      const token = await getToken?.();
      const auth = new AuthRepository(
        {
          ...CsrfTokenProvider,
          getAuthToken: () => token ?? undefined,
        },
        baseUrlNoTrailingSlash,
        http
      );

      try {
        log.debug(`[AuthStore] ${new Date()}: JWT refresh`);
        await auth.issueJwtForApp();
        // Clear server error on success
        set({ hasServerError: false, serverErrorStatus: null });
        return auth.tokenExpiration ?? null;
      } catch (error) {
        // Check for 500 error
        if (error && typeof error === 'object' && 'status' in error) {
          const status = (error as any).status as number;
          if (status === 500) {
            log.error(`[AuthStore] Server error ${status} when getting JWT:`, error);
            set({ hasServerError: true, serverErrorStatus: status });
            throw error; // Re-throw to prevent automatic retries
          }
        }
        log.error('[AuthStore] Error getting JWT:', error);
        return null;
      }
    },

    /**
     * Fetches current user data from the server.
     * This includes roles, privileges, and user information.
     * For anonymous users, this will fetch their community role and associated privileges.
     *
     * The function will:
     * 1. Attempt to fetch user data with current credentials
     * 2. If that fails, attempt to fetch community user privileges
     * 3. Store the fetched data in local storage
     * 4. Update the store state with the fetched data
     *
     * @throws {Error} If both authenticated and community user data fetch fail
     */
    fetchUserDataFromServer: async (options) => {
      const allowCommunityFallback = options?.allowCommunityFallback !== false;
      const configStore = useConfigStore.getState();
      if (!configStore.systemConfig) return;

      const baseUrlNoTrailingSlash = configStore.getBaseUrlNoTrailingSlash();

      try {
        const repo = new UserRepository(CsrfTokenProvider, baseUrlNoTrailingSlash);
        const userResponse = await repo.getCurrent();

        if (userResponse) {
          const privileges = userResponse.roles.find((r) => r.id === userResponse.selectedRole)?.privileges;
          const { authMethod: rawAuthMethod, ...userRest } = userResponse;
          const authMethod = normalizeAuthMethod(rawAuthMethod);
          const currentUser: ICurrentUser = {
            ...userRest,
            authMethod,
            isAnonymous: !userResponse.eppn,
          };

          if (privileges) {
            set({
              currentUser,
              privileges: privileges,
              error: null,
              hasServerError: false,
              serverErrorStatus: null,
              viewingPublicSession: !userResponse.eppn,
            });

            storeUserData({
              expiration: repo.tokenExpiration ?? '',
              privileges: privileges,
              user: currentUser,
            });
          }
          set({ isLoading: false });
        }
      } catch (error) {
        // Check for 500 error
        if (error && typeof error === 'object' && 'status' in error) {
          const status = (error as { status: number }).status;
          if (status === 500) {
            log.error(`[AuthStore] Server error ${status} when fetching user data:`, error);
            set({
              hasServerError: true,
              serverErrorStatus: status,
              isLoading: false,
            });
            return;
          }
        }

        if (error instanceof Error) {
          log.error('[AuthStore] Error refreshing user data:', {
            message: formatErrorMessage(error),
            name: error.name,
            stack: error.stack,
          });
        } else {
          log.error('[AuthStore] Unknown error refreshing user data:', formatErrorMessage(error));
        }

        if (!allowCommunityFallback) {
          throw error;
        }

        // Community fallback only for init / explicit anonymous flows — not session recovery
        try {
          const repo = new UserRepository(CsrfTokenProvider, baseUrlNoTrailingSlash);
          const communityUser = await repo.getCurrent();

          if (communityUser) {
            const communityPrivileges = communityUser.roles.find(
              (r) => r.id === communityUser.selectedRole
            )?.privileges;
            const { authMethod: rawAuthMethod, ...communityRest } = communityUser;
            const communityCurrentUser: ICurrentUser = {
              ...communityRest,
              authMethod: normalizeAuthMethod(rawAuthMethod),
              isAnonymous: true,
            };
            set({
              privileges: communityPrivileges ?? [],
              currentUser: communityCurrentUser,
              error: null,
              viewingPublicSession: true,
            });
            storeUserData({
              expiration: repo.tokenExpiration ?? '',
              privileges: communityPrivileges ?? [],
              user: communityCurrentUser,
            });
          }
        } catch (communityError) {
          log.error('[AuthStore] Failed to get community user privileges:', communityError);
          set({
            privileges: [],
            currentUser: undefined,
            error: formatErrorMessage(error, 'Failed to refresh user data'),
          });
        }
      } finally {
        set({ isLoading: false });
      }
    },

    notifySessionLost: (reason) => {
      if (get().sessionLostDialogOpen && get().sessionLostReason === reason) {
        return;
      }
      log.info('[AuthStore] Session lost notification', { reason });
      invalidateCachedOption('projects');
      set({
        sessionLostReason: reason,
        sessionLostDialogOpen: true,
      });
    },

    clearSessionLost: () => {
      set({
        sessionLostReason: null,
        sessionLostDialogOpen: false,
      });
    },

    acceptPublicSession: async () => {
      const configStore = useConfigStore.getState();
      if (!configStore.systemConfig) return;

      const baseUrlNoTrailingSlash = configStore.getBaseUrlNoTrailingSlash();
      try {
        const http = new HttpClient();
        const auth = new AuthRepository(CsrfTokenProvider, baseUrlNoTrailingSlash, http);
        await auth.signout();
        await get().fetchUserDataFromServer({ allowCommunityFallback: true });
        persistViewingPublicSession(true);
        set({ viewingPublicSession: true });
        get().clearSessionLost();
      } catch (error) {
        log.error('[AuthStore] acceptPublicSession failed:', error);
      }
    },

    attemptSessionRecovery: async () => {
      if (sessionRecoveryPromise) {
        return sessionRecoveryPromise;
      }

      sessionRecoveryPromise = (async () => {
        const wasSignedIn = userHasEppn(get().currentUser);
        try {
          if (!isStoredSessionExpirationValid()) {
            const expiration = await get().getJwt(get().getToken);
            if (!expiration) {
              if (wasSignedIn) {
                get().notifySessionLost('degraded');
              }
              return false;
            }
          }

          await get().fetchUserDataFromServer({ allowCommunityFallback: false });

          if (wasSignedIn && !userHasEppn(get().currentUser)) {
            get().notifySessionLost('degraded');
            return false;
          }

          if (wasSignedIn && userHasEppn(get().currentUser)) {
            return true;
          }

          // Anonymous / community: JWT cookie was refreshed — allow HttpClient to retry 401s.
          if (!wasSignedIn) {
            return true;
          }

          return false;
        } catch (error) {
          log.error('[AuthStore] attemptSessionRecovery failed:', error);
          if (wasSignedIn) {
            get().notifySessionLost('degraded');
          }
          return false;
        }
      })();

      try {
        return await sessionRecoveryPromise;
      } finally {
        sessionRecoveryPromise = null;
      }
    },

    // Auth actions
    login: async () => {
      const { msalInstance } = get();
      if (!msalInstance) return;

      const configStore = useConfigStore.getState();
      if (!configStore.systemConfig?.msalConfig?.scope || get().isInProgress) return;

      set({ isInProgress: true, error: null });
      try {
        await msalInstance.loginPopup({
          scopes: [configStore.systemConfig.msalConfig.scope],
        });

        get().clearViewingPublicSession();
        await get().getJwt(get().getToken);
        await get().fetchUserDataFromServer();
        get().clearSessionLost();
      } catch (error) {
        // Check for 500 error
        if (error && typeof error === 'object' && 'status' in error) {
          const status = (error as { status: number }).status;
          if (status === 500) {
            log.error(`[AuthStore] Server error ${status} during login:`, error);
            set({
              hasServerError: true,
              serverErrorStatus: status,
              error: 'Server error during login',
            });
            return; // Stop here - don't retry automatically
          }
        }
        log.error('[AuthStore] Login failed:', error);
        set({ error: 'Login failed' });
      } finally {
        set({ isInProgress: false });
      }
    },

    loginRedirect: async (explicitReturnPath?: string) => {
      const { msalInstance } = get();
      if (!msalInstance || typeof window === 'undefined') return;

      const configStore = useConfigStore.getState();
      if (!configStore.systemConfig?.msalConfig?.scope || get().isInProgress) return;

      const fallback = `${window.location.pathname}${window.location.search}${window.location.hash}`;
      const target = explicitReturnPath && explicitReturnPath.startsWith('/') ? explicitReturnPath : fallback;

      set({ isInProgress: true, error: null });
      get().clearViewingPublicSession();
      try {
        try {
          scopedSessionStorage.setItem(POST_MSAL_LOGIN_RETURN_PATH_KEY, target);
        } catch (storageError) {
          log.warn('[AuthStore] Could not store post-login return path:', storageError);
        }

        await msalInstance.loginRedirect({
          scopes: [configStore.systemConfig.msalConfig.scope],
        });
        // Leave isInProgress true until the document unloads; avoids flashing the access-denied UI.
      } catch (error) {
        log.error('[AuthStore] loginRedirect failed:', error);
        set({ error: 'Login failed', isInProgress: false });
        try {
          scopedSessionStorage.removeItem(POST_MSAL_LOGIN_RETURN_PATH_KEY);
        } catch {
          /* ignore */
        }
      }
    },

    /**
     * Signs out of LiST and optionally the upstream IdP session.
     * Always resets the LiST JWT to anonymous (when allowed), then either navigates Home or
     * redirects through Entra / Shibboleth logout for `scope: 'full'`.
     */
    logout: async (options?: LogoutOptions) => {
      if (typeof window === 'undefined') {
        return;
      }

      const scope: LogoutScope = options?.scope ?? 'list';
      const redirectToHome = options?.redirectToHome !== false;

      try {
        const configStore = useConfigStore.getState();
        const basePath = configStore.systemConfig?.basePath ?? '';
        const baseUrlNoTrailingSlash = configStore.getBaseUrlNoTrailingSlash();
        const authMethod = normalizeAuthMethod(get().currentUser?.authMethod);
        const homeUrl = resolveHomeUrl(basePath);

        removeUserDataFromStorage({ clearMsalCache: false });

        const http = new HttpClient();
        const auth = new AuthRepository(CsrfTokenProvider, baseUrlNoTrailingSlash, http);

        try {
          await auth.signout();
        } catch (authError) {
          log.error('[AuthStore] LiST signout failed:', authError);
          set({
            currentUser: undefined,
            privileges: [],
            error: null,
          });
        }

        if (scope === 'list') {
          persistViewingPublicSession(true);
          set({ viewingPublicSession: true });
        } else {
          persistViewingPublicSession(false);
          set({ viewingPublicSession: false });
        }

        if (scope === 'full' && authMethod === 'msal') {
          const { msalInstance } = get();
          if (msalInstance) {
            try {
              await msalInstance.logoutRedirect({
                postLogoutRedirectUri: homeUrl,
              });
              return;
            } catch (msalError) {
              log.warn('[AuthStore] MSAL logout redirect failed, continuing with local cleanup:', msalError);
            }
          }
          clearMsalCacheFromStorage();
        } else if (scope === 'full' && authMethod === 'shibboleth') {
          const returnPath = resolveShibbolethLogoutReturnPath(basePath);
          window.location.assign(buildShibbolethLogoutUrl(returnPath));
          return;
        }

        try {
          await get().fetchUserDataFromServer();
        } catch (fetchError) {
          log.error('[AuthStore] Failed to load anonymous session after sign-out:', fetchError);
          set({
            currentUser: undefined,
            privileges: [],
            error: null,
          });
        }

        if (scope === 'full') {
          clearMsalCacheFromStorage();
        }

        if (redirectToHome) {
          navigateToHome(basePath);
        }
      } catch (error) {
        log.error('[AuthStore] Logout failed:', error);
        set({ error: 'Logout failed' });
      }
    },

    /**
     * Switches the user's role to the specified role ID.
     * This is typically used by administrators to switch between different privilege sets.
     *
     * @param selectedRole - The ID of the role to switch to
     * @throws {Error} If role switch fails or user lacks permission
     */
    switchRole: async (selectedRole: number) => {
      const configStore = useConfigStore.getState();
      const baseUrlNoTrailingSlash = configStore.getBaseUrlNoTrailingSlash();

      try {
        set({ isLoading: true });
        const http = new HttpClient();
        const auth = new AuthRepository(CsrfTokenProvider, baseUrlNoTrailingSlash, http);
        await auth.switchRole(selectedRole);
        await get().fetchUserDataFromServer();
      } catch (error) {
        log.error('[AuthStore] Role switch failed:', error);
        set({ error: 'Failed to switch role' });
      } finally {
        set({ isLoading: false });
      }
    },

    /**
     * Impersonates another user by their ID.
     * This is an administrative function that allows viewing the system as another user.
     *
     * @param userId - The ID of the user to impersonate, or null to stop impersonation
     * @throws {Error} If impersonation fails or user lacks permission
     */
    impersonateUser: async (userId: string | null) => {
      const configStore = useConfigStore.getState();
      const baseUrlNoTrailingSlash = configStore.getBaseUrlNoTrailingSlash();

      try {
        const http = new HttpClient();
        const auth = new AuthRepository(CsrfTokenProvider, baseUrlNoTrailingSlash, http);
        await auth.impersonate(userId);
        await get().fetchUserDataFromServer();
      } catch (error) {
        log.error('[AuthStore] Impersonation failed:', error);
        set({ error: 'Impersonation failed' });
      }
    },

    /**
     * Stops impersonating another user and returns to the original user context.
     *
     * @throws {Error} If stopping impersonation fails or user is not currently impersonating
     */
    stopImpersonate: async () => {
      const configStore = useConfigStore.getState();
      const baseUrlNoTrailingSlash = configStore.getBaseUrlNoTrailingSlash();

      try {
        const http = new HttpClient();
        const auth = new AuthRepository(CsrfTokenProvider, baseUrlNoTrailingSlash, http);
        await auth.stopImpersonate();
        await get().fetchUserDataFromServer();
      } catch (error) {
        log.error('[AuthStore] Stop impersonation failed:', error);
        set({ error: 'Failed to stop impersonation' });
      }
    },

    // Existing async actions
    /**
     * Acquires an access token from MSAL.
     * This is used to authenticate API requests and can be exchanged for a JWT.
     *
     * @returns {Promise<string | null>} The access token or null if acquisition fails
     * @throws {Error} If token acquisition fails
     */
    getToken: async () => {
      if (isViewingPublicSession(get)) {
        return null;
      }

      const { msalInstance, isClient } = get();
      if (!isClient || !msalInstance) return null;

      const configStore = useConfigStore.getState();
      if (!configStore.systemConfig?.msalConfig?.scope) return null;

      try {
        const account = msalInstance.getAllAccounts()[0];
        if (!account) {
          log.debug('[AuthStore] No account found - user is not signed in.');
          return null;
        }

        const response = await msalInstance.acquireTokenSilent({
          scopes: [configStore.systemConfig.msalConfig.scope],
          account: account,
        });

        return response.accessToken;
      } catch (error) {
        log.error('[AuthStore] Error acquiring token:', error);
        return null;
      }
    },

    clearServerError: () => {
      set({ hasServerError: false, serverErrorStatus: null, error: null });
    },

    retryAuth: async () => {
      get().clearServerError();
      try {
        await get().getJwt(get().getToken);
        await get().fetchUserDataFromServer();
      } catch (error) {
        // Error handling is done in getJwt and fetchUserDataFromServer
        log.error('[AuthStore] Retry auth failed:', error);
      }
    },
  };
});

/**
 * Returns true once {@link ensureAppAuthReady} has completed (AuthToken cookie + user context).
 */
export function useAppAuthReady(): boolean {
  return useAuthStore((state) => state.isInitialized);
}
